import { MouseEvent, useEffect, useRef, useState } from 'react';
import { useOnClickOutside } from '../../hooks/use-clickoutside.hook';
import { Data } from '../../hooks/use-data2.hook';
import { GeoLevel, Indicator } from '../../types';
import { DownloadIndicatorButton } from '../DownloadIndicatorButton';
import { XAxisIcon, YAxisIcon, SizeIcon, ColorIcon } from '../Icons';
import { Portal } from '../Portal';

import { getRouteApi } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import { useToolStore } from '../Tool/tool.store';
import { useShallow } from 'zustand/shallow';
import { FileSelect2 } from '../FileViewer/FileSelect2';

const routeApi = getRouteApi('/tool/');

type TooltipButtonProps = {
    indicator: Indicator;
};
function TooltipButton({ indicator }: TooltipButtonProps) {
    const { t, i18n } = useTranslation();

    const [viewerIsOpen, setViewerIsOpen] = useState<boolean>(false);
    const [viewerPos, setViewerPos] = useState<{ x: number; y: number }>({
        x: 500,
        y: 500,
    });

    const handleClick = (e: MouseEvent) => {
        const rect = (e.target as HTMLButtonElement).getBoundingClientRect();
        setViewerPos({
            x: rect.left + rect.width + 5,
            y: rect.top + rect.height / 2,
        });
        setViewerIsOpen(!viewerIsOpen);
    };

    const portalDivRef = useRef<HTMLDivElement | null>(null);
    const buttonRef = useRef<HTMLButtonElement | null>(null);

    useOnClickOutside(portalDivRef, (e) => {
        setViewerIsOpen(false);
    });

    useEffect(() => {
        if (!viewerIsOpen) return;
        const handleKeyDown = (e: KeyboardEvent) => {
            if (e.key === 'Escape') {
                e.preventDefault();
                setViewerIsOpen(false);
                buttonRef.current?.focus();
            }
        };
        document.addEventListener('keydown', handleKeyDown);
        return () => document.removeEventListener('keydown', handleKeyDown);
    }, [viewerIsOpen]);

    return (
        <div>
            {viewerIsOpen && (
                <Portal>
                    <div className="fixed inset-0 bg-transparent"></div>
                    <div
                        ref={portalDivRef}
                        className="absolute bg-white shadow-xl rounded-lg border border-opacity-50 p-3 max-w-md"
                        style={{
                            top: `${viewerPos.y}px`,
                            left: `${viewerPos.x}px`,
                            transform: 'translateY(-50%)',
                            width: '400px',
                        }}
                    >
                        <h2 className="font-bold mb-3 text-lg">
                            {indicator.label?.[i18n.language] ??
                                indicator.label?.['sv']}
                        </h2>
                        {indicator.description && (
                            <p className="mb-3 whitespace-pre-line">
                                {indicator.description?.[i18n.language] ??
                                    indicator.description?.['sv']}
                            </p>
                        )}
                        {(indicator.sourceUrl || indicator.sourceName) && (
                            <p>
                                Källa:{' '}
                                {indicator.sourceUrl ? (
                                    <a
                                        href={indicator.sourceUrl}
                                        target="_blank"
                                        className="link"
                                    >
                                        {indicator.sourceName ??
                                            indicator.sourceUrl}
                                    </a>
                                ) : (
                                    indicator.sourceName
                                )}
                            </p>
                        )}
                    </div>
                </Portal>
            )}
            <button
                ref={buttonRef}
                onClick={handleClick}
                type="button"
                className={`btn btn-xs btn-circle btn-primary text-white ${
                    viewerIsOpen ? 'btn-secondary' : ''
                }`}
                aria-label={`${t('info')} ${indicator.label[i18n.language] ?? indicator.label['sv']}`}
            >
                ?
            </button>
        </div>
    );
}

type Props = {
    data: Data;
    indicatorsMap: Record<string, Indicator>;
    geoLevel: GeoLevel;
    featureFilter: string[];
};

export function VarSelect({
    data,
    indicatorsMap,
    geoLevel,
    featureFilter,
}: Props) {
    const { t } = useTranslation();

    const toolStore = useToolStore(
        useShallow((state) => ({
            setIndicatorById: state.setIndicatorById,
            getDataOptions: state.getDataOptions,
            indicatorsMap: state.indicatorsMap,
        }))
    );

    const dataOptions = toolStore.getDataOptions();

    return (
        <div className="grid grid-cols-[1fr_auto] gap-2">
            <FileSelect2
                indicators={Object.values(toolStore.indicatorsMap)}
                value={dataOptions.indicators.yVar.id}
                label="y"
                labelIcon={<YAxisIcon />}
                indicatorsMap={indicatorsMap}
                onChange={(value) => {
                    toolStore.setIndicatorById({
                        key: 'y',
                        indicatorId: value,
                    });
                }}
                tooltip={t('valj-y')}
            />

            <div className="flex flex-col gap-1 justify-center">
                <TooltipButton indicator={dataOptions.indicators.yVar} />
                <DownloadIndicatorButton
                    featureFilter={featureFilter}
                    indicator={data.options.indicators.yVar}
                    geoLevel={geoLevel}
                    data={data}
                />
            </div>

            <FileSelect2
                indicators={Object.values(toolStore.indicatorsMap)}
                value={dataOptions.indicators.xVar.id}
                label="x"
                labelIcon={<XAxisIcon />}
                indicatorsMap={indicatorsMap}
                onChange={(value) => {
                    toolStore.setIndicatorById({
                        key: 'x',
                        indicatorId: value,
                    });
                }}
                tooltip={t('valj-x')}
            />

            <div className="flex flex-col gap-1 justify-center">
                <TooltipButton indicator={dataOptions.indicators.xVar} />
                <DownloadIndicatorButton
                    indicator={data.options.indicators.xVar}
                    geoLevel={geoLevel}
                    data={data}
                    featureFilter={featureFilter}
                />
            </div>

            <FileSelect2
                indicators={Object.values(toolStore.indicatorsMap)}
                value={dataOptions.indicators.rVar.id}
                label={t('storlek')}
                labelIcon={<SizeIcon />}
                indicatorsMap={indicatorsMap}
                onChange={(value) => {
                    toolStore.setIndicatorById({
                        key: 'r',
                        indicatorId: value,
                    });
                }}
                tooltip={t('valj-r')}
            />

            <div className="flex flex-col gap-1 justify-center">
                <TooltipButton indicator={dataOptions.indicators.rVar} />
                <DownloadIndicatorButton
                    indicator={data.options.indicators.rVar}
                    geoLevel={geoLevel}
                    data={data}
                    featureFilter={featureFilter}
                />
            </div>

            <FileSelect2
                indicators={Object.values(toolStore.indicatorsMap)}
                value={
                    dataOptions.indicators.cVar
                        ? dataOptions.indicators.cVar.id
                        : 'kommun'
                }
                label={t('farg')}
                labelIcon={<ColorIcon className="drop-shadow-lg" />}
                indicatorsMap={indicatorsMap}
                onChange={(value) => {
                    toolStore.setIndicatorById({
                        key: 'c',
                        indicatorId: value,
                    });
                }}
                tooltip={t('valj-c')}
                alternateActions={[
                    {
                        label: 'Kommun',
                        onClick: () => {
                            toolStore.setIndicatorById({
                                key: 'c',
                                indicatorId: null,
                            });
                        },
                    },
                ]}
            />

            <div className="flex flex-col gap-1 justify-center">
                {dataOptions.indicators.cVar && (
                    <TooltipButton indicator={dataOptions.indicators.cVar} />
                )}
                {dataOptions.indicators.cVar && (
                    <DownloadIndicatorButton
                        featureFilter={featureFilter}
                        indicator={dataOptions.indicators.cVar}
                        geoLevel={geoLevel}
                        data={data}
                    />
                )}
            </div>
        </div>
    );
}
